import { Text } from '@react-three/drei';

import { RANKS } from '@/types/chart';

interface GridLabelsProps {
  barSize: number;
  gap: number;
}

/**
 * Renders the A-K-Q-J-T-9-8-7-6-5-4-3-2 labels along the edges of the grid.
 */
export default function GridLabels({ barSize, gap }: GridLabelsProps) {
  const gridSize = 13;
  const totalSize = gridSize * barSize + (gridSize - 1) * gap;
  const offset = -totalSize / 2 + barSize / 2;
  const labelOffset = 0.8; // How far from the grid edge
  const typeOffset = 1.4; // How far out for type labels

  return (
    <group>
      {/* Column labels (top edge, along X axis) */}
      {RANKS.map((rank, col) => (
        <Text
          key={`col-top-${rank}`}
          position={[offset + col * (barSize + gap), 0, -totalSize / 2 - labelOffset]}
          fontSize={0.3}
          color="#888888"
          anchorX="center"
          anchorY="middle"
          rotation={[-Math.PI / 2, 0, 0]}
        >
          {rank}
        </Text>
      ))}

      {/* "Suited" label (top edge) */}
      <Text
        position={[0, 0, -totalSize / 2 - typeOffset]}
        fontSize={0.35}
        color="#999999"
        anchorX="center"
        anchorY="middle"
        rotation={[-Math.PI / 2, 0, 0]}
      >
        Suited
      </Text>

      {/* Column labels (bottom edge, along X axis) */}
      {RANKS.map((rank, col) => (
        <Text
          key={`col-bottom-${rank}`}
          position={[offset + col * (barSize + gap), 0, totalSize / 2 + labelOffset]}
          fontSize={0.3}
          color="#888888"
          anchorX="center"
          anchorY="middle"
          rotation={[-Math.PI / 2, 0, 0]}
        >
          {rank}
        </Text>
      ))}

      {/* "Offsuit" label (bottom edge) */}
      <Text
        position={[0, 0, totalSize / 2 + typeOffset]}
        fontSize={0.35}
        color="#999999"
        anchorX="center"
        anchorY="middle"
        rotation={[-Math.PI / 2, 0, 0]}
      >
        Offsuit
      </Text>

      {/* Row labels (left edge, along Z axis) */}
      {RANKS.map((rank, row) => (
        <Text
          key={`row-left-${rank}`}
          position={[-totalSize / 2 - labelOffset, 0, offset + row * (barSize + gap)]}
          fontSize={0.3}
          color="#888888"
          anchorX="center"
          anchorY="middle"
          rotation={[-Math.PI / 2, 0, 0]}
        >
          {rank}
        </Text>
      ))}

      {/* "Offsuit" label (left edge) */}
      <Text
        position={[-totalSize / 2 - typeOffset, 0, 0]}
        fontSize={0.35}
        color="#999999"
        anchorX="center"
        anchorY="middle"
        rotation={[-Math.PI / 2, 0, Math.PI / 2]}
      >
        Offsuit
      </Text>

      {/* Row labels (right edge, along Z axis) */}
      {RANKS.map((rank, row) => (
        <Text
          key={`row-right-${rank}`}
          position={[totalSize / 2 + labelOffset, 0, offset + row * (barSize + gap)]}
          fontSize={0.3}
          color="#888888"
          anchorX="center"
          anchorY="middle"
          rotation={[-Math.PI / 2, 0, 0]}
        >
          {rank}
        </Text>
      ))}

      {/* "Suited" label (right edge) */}
      <Text
        position={[totalSize / 2 + typeOffset, 0, 0]}
        fontSize={0.35}
        color="#999999"
        anchorX="center"
        anchorY="middle"
        rotation={[-Math.PI / 2, 0, -Math.PI / 2]}
      >
        Suited
      </Text>

      {/* Watermark */}
      <Text
        position={[totalSize / 2 + typeOffset + 0.25, 0.01, totalSize / 2 + typeOffset]}
        fontSize={0.25}
        color="#3a3a3a"
        anchorX="right"
        anchorY="top"
        rotation={[-Math.PI / 2, 0, 0]}
      >
        handscape.poker
      </Text>
    </group>
  );
}
